import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDate, formatDateTime } from '../lib/format';
import type { AccessPerson } from '../lib/executive';
import { FormPage } from '../components/FormPage';
import { Badge, Detail, Field, Textarea, useToast } from '../components/ui';

const BACK = '/settings?tab=executive';

function usePerson(userId: string) {
  return useQuery({ queryKey: ['executive-access', 'person', userId], queryFn: () => api.get<AccessPerson>(`/dashboard-access/${userId}`) });
}

/** Who, their role, and where their access stands — above both forms. */
function PersonSummary({ person }: { person: AccessPerson }) {
  const { t, locale, localized } = useI18n();
  return (
    <dl className="divide-y divide-[var(--line)]">
      <Detail label={t('common.employee')}>{localized(person.user)}</Detail>
      <Detail label={t('exec.col.email')}>
        <span dir="ltr">{person.user.email}</span>
      </Detail>
      <Detail label={t('exec.col.role')}>
        <span className="inline-flex flex-wrap justify-end gap-1">
          {person.roles.map((r) => (
            <Badge key={r.code} tone={r.code === 'ADMIN' ? 'brand' : 'neutral'}>
              {locale === 'ar' ? r.nameAr || r.name : r.name}
            </Badge>
          ))}
        </span>
      </Detail>
      <Detail label={t('exec.col.access')}>{t(`exec.state.${person.access.state}`)}</Detail>
      {person.access.grantedAt && (
        <Detail label={t('exec.col.grantedBy')}>
          {person.access.grantedBy ? localized(person.access.grantedBy) : '—'} · {formatDate(person.access.grantedAt, locale)}
        </Detail>
      )}
      <Detail label={t('exec.col.lastSignIn')}>{person.lastDashboardSignInAt ? formatDateTime(person.lastDashboardSignInAt, locale) : '—'}</Detail>
    </dl>
  );
}

function History({ person }: { person: AccessPerson }) {
  const { t, locale, localized } = useI18n();
  if (!person.history.length) return null;
  return (
    <div className="mt-5">
      <p className="mb-2 text-body-sm font-medium text-[var(--text-secondary)]">{t('exec.historyTitle')}</p>
      <ul className="space-y-1 text-body-sm text-muted">
        {person.history.map((h) => (
          <li key={h.id}>
            {formatDateTime(h.createdAt, locale)} — {t(`exec.event.${h.kind}`)}
            {h.actor ? ` · ${localized(h.actor)}` : ''}
            {h.reason ? ` · ${h.reason}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Route: `/settings/executive-access/:userId/grant` — gives an active Administrator Dashboard Access, with an
 * optional reason. Anyone else is refused by the API (DASHBOARD_NOT_ELIGIBLE); it needs no dashboard session.
 */
export function ExecutiveAccessGrantPage() {
  const { userId = '' } = useParams();
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const [reason, setReason] = useState('');
  const { data: person, isLoading, error, refetch } = usePerson(userId);
  const grant = useMutation({
    mutationFn: () => api.post<AccessPerson & { changed: boolean }>(`/dashboard-access/${userId}/grant`, { reason: reason.trim() || undefined }),
    onSuccess: (r) => {
      toast.success(r.changed ? t('exec.granted', { name: localized(r.user) }) : t('exec.alreadyGranted'));
      void queryClient.invalidateQueries({ queryKey: ['executive-access'] });
      // The link to the dashboard shows at once when it was granted to the reader.
      void refreshUser();
      navigate(BACK);
    },
    onError: (e: Error) => toast.error(e instanceof ApiError && e.code === 'DASHBOARD_NOT_ELIGIBLE' ? t('exec.notEligible') : e.message),
  });
  return (
    <FormPage
      title={t('exec.grantTitle')}
      subtitle={t('exec.grantSubtitle')}
      backTo={BACK}
      backLabel={t('exec.back')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={person && <PersonSummary person={person} />}
      submitLabel={t('exec.grant')}
      onSubmit={() => grant.mutate()}
      submitting={grant.isPending}
      disabled={!person?.canGrant}
    >
      <Field label={t('exec.grantReason')} hint={t('exec.grantReasonHint')}>
        <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
      </Field>
      {person && !person.canGrant && (
        <p className="mt-3 text-body-sm text-warning-fg dark:text-warning-dark">{person.access.state === 'GRANTED' ? t('exec.alreadyGranted') : t('exec.notEligible')}</p>
      )}
      {person && <History person={person} />}
    </FormPage>
  );
}

/**
 * Route: `/settings/executive-access/:userId/revoke` — takes Dashboard Access away, with a reason. Their
 * dashboard sessions end at once; their admin and Finance sessions are not touched.
 */
export function ExecutiveAccessRevokePage() {
  const { userId = '' } = useParams();
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const [reason, setReason] = useState('');
  const { data: person, isLoading, error, refetch } = usePerson(userId);
  const revoke = useMutation({
    mutationFn: () => api.post<AccessPerson>(`/dashboard-access/${userId}/revoke`, { reason: reason.trim() }),
    onSuccess: (r) => {
      toast.success(t('exec.revoked', { name: localized(r.user) }));
      void queryClient.invalidateQueries({ queryKey: ['executive-access'] });
      void refreshUser();
      navigate(BACK);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <FormPage
      title={t('exec.revokeTitle')}
      subtitle={t('exec.revokeSubtitle')}
      backTo={BACK}
      backLabel={t('exec.back')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={person && <PersonSummary person={person} />}
      submitLabel={t('exec.revoke')}
      onSubmit={() => revoke.mutate()}
      submitting={revoke.isPending}
      disabled={person?.access.state !== 'GRANTED' || !reason.trim()}
      destructive
    >
      <Field label={t('exec.revokeReason')} hint={t('exec.revokeReasonHint')} required>
        <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
      </Field>
      {person && <History person={person} />}
    </FormPage>
  );
}
